import { push } from "connected-react-router";
import moment from "moment";

import CSV from "csv-string";

import { toast } from "react-toastify";

import * as DeployAPI from "api/DeployAPI";
import * as ModelGroupAPI from "api/ModelGroupAPI";
import * as ProjectAPI from "api/ProjectAPI";
import * as ModelAPI from "api/ModelAPI";
import * as ScoreAPI from "api/ScoreAPI";

import * as runReducer from "containers/Run/reducer";
import * as globalReducer from "containers/App/reducer";
import * as modelGroupReducer from "containers/ModelGroup/reducer";
import * as projectReducer from "containers/Project/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as accountsReducer from "containers/Accounts/reducer";

import { filterOtherUsers, getIsMarketplace } from "containers/Marketplace/reducer";

import { delay } from "utils/promise";
import { AibleClientFactory } from "client/aible/factory";
import { project } from "types/relation";
import { getReadOptions } from "client/aible/dataset";

// Number of megabytes the csv chunks can be
// If the file is larger, we will split them into these sizes
const CSV_CHUNK_SIZE = 2;
const MAX_CSV_SIZE = 10;
const MAX_GZIP_SIZE = 3;

const UPDATE_FIELD = "deployments/UPDATE_FIELD";
const UPDATE_DEPLOYMENT = "deployments/UPDATE_DEPLOYMENT";
const DELETE_DEPLOYMENT = "deployments/DELETE_DEPLOYMENT";
const UPDATE_DEPLOYMENTS = "deployments/UPDATE_DEPLOYMENTS";
const RESET_DEPLOYMENTS = "deployments/RESET_DEPLOYMENTS";

const initialState = {};

export default function predictReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    case UPDATE_DEPLOYMENT:
      return {
        ...state,
        [action.id]: {
          ...action.data
        }
      };
    case DELETE_DEPLOYMENT:
      return {
        ...Object.keys(state).reduce(
          (newObj, key) => (key !== action.id ? { ...newObj, [key]: state[key] } : newObj),
          {}
        )
      };
    case UPDATE_DEPLOYMENTS:
      return {
        ...state,
        ...action.data
      };
    case RESET_DEPLOYMENTS:
      return {
        ...initialState
      };
    default:
      return state;
  }
}

/* Selectors */
export function getGlobal(state) {
  return state.deployments;
}

export function getDeploymentsArr(state, filterForProjects) {
  const deployments = getGlobal(state);

  return Object.keys(deployments)
    .map((key) => deployments[key])
    .filter(
      (deployments) =>
        deployments.deleted !== true &&
        deployments.status !== "DONE" &&
        (!filterForProjects || projectReducer.getProject(state, deployments.project_id))
    )
    .sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
}

export function getGroupDeploymentsArr(state) {
  const deployments = getGlobal(state);

  return Object.keys(deployments)
    .filter((key) => key.indexOf("group_") !== -1)
    .map((key) => deployments[key]);
}

// DO NOT USE THIS IF YOU CAN AVOID IT
// Just check for a deployed scenario and an account executor
export function getProjectDeployment(state, projectId) {
  const deployedScenario = projectReducer.getDeployedScenarioIndex(state, projectId);
  const accountId = projectReducer.getProjectAccountId(state, projectId);
  // account executor is deprecated
  const accountExecutor = accountsReducer.getAccountExecutor(state, accountId);
  const accountAPIEndpoint = accountsReducer.getAccountAPIEndpoint(state, accountId);

  if (!isNaN(deployedScenario) && accountExecutor) {
    return {
      ...accountExecutor,
      account_id: accountId,
      endpoint_headers: { ...accountExecutor?.endpoint_headers },
      status: accountExecutor?.status?.toUpperCase(),
      endpoint_url: `${accountAPIEndpoint}/projects/${projectId}`,
      project_id: projectId
    };
  }

  return state.deployments[projectId];
}

export function getDeployment(state, jobId, modelId) {
  return state.deployments[jobId + "_" + modelId];
}

export function getModelGroupDeployment(state, jobId, modelId) {
  return state.deployments["group_" + jobId + "_" + modelId];
}

export function getDeploymentsFromJobId(state, jobId) {
  return getDeploymentsArr(state).filter((d) => jobId && d.job_id === Number(jobId));
}

export function getCurrentDeploymentId(state, jobId) {
  const deployments = getDeploymentsFromJobId(state, jobId);
  const selectedModel = runReducer.getSelModel(state, jobId);
  for (let deployment of deployments) {
    if (deployment.model_name === selectedModel.id) {
      return deployment.id;
    }
  }
  return null;
}

export function getModelGroupDeploy(state, jobId) {
  const modelGroup = modelGroupReducer.getModelGroup(state, jobId, false);

  return modelGroup && modelGroup.head
    ? getModelGroupDeployment(state, modelGroup.head.job_id, modelGroup.head.model_name)
    : undefined;
}

/* Actions */
export function updateDeployments(deployments) {
  return { type: UPDATE_DEPLOYMENTS, data: deployments };
}

export function resetDeployments() {
  return { type: RESET_DEPLOYMENTS };
}

export function updateDeployment(deployment) {
  const id =
    deployment.project_id !== undefined && deployment.project_id !== null
      ? deployment.project_id
      : deployment.job_id + "_" + deployment.model_name;

  return {
    type: UPDATE_DEPLOYMENT,
    id: id,
    data: deployment
  };
}

export function updateGroupDeployment(deployment) {
  return {
    type: UPDATE_DEPLOYMENT,
    id: "group_" + deployment.job_id + "_" + deployment.model_name,
    data: { ...deployment, isGroup: true }
  };
}

export function deleteDeployment(jobId, modelName, isGroup = false) {
  return {
    type: DELETE_DEPLOYMENT,
    id: (isGroup ? "group_" : "") + jobId + "_" + modelName
  };
}

function transformDeployments(state, deployments) {
  let retDeployments = {};

  Object.keys(deployments).map((deployment) => {
    retDeployments[deployment] = transformDeployment(state, deployments[deployment]);
  });

  return retDeployments;
}

function transformDeployment(state, deployment) {
  let label = "Deployment";

  if (deployment.project_id) {
    const project = projectReducer.getProject(state, deployment.project_id) || {};

    if (project.name) {
      label = project.name;
    }
  } else if (deployment.model_name) {
    label = getModelName(deployment);
  }

  return {
    ...deployment,
    label: label,
    endpoint_url: deployment.endpoint_url ?? "legacy_placeholder"
  };
}

export function getModelName(deployment) {
  return deployment.model_name;
}

export function fetchAllDeployments(jobId) {
  return (dispatch, getState) => {
    let userId = null;
    const folderId = foldersReducer.getSelectedFolder(getState());
    return DeployAPI.getAllDeploy(userId).then((response) => {
      if (folderId !== foldersReducer.getSelectedFolder(getState())) {
        return;
      }
      let deployments = {};

      const filteredRes = response && (getIsMarketplace() ? filterOtherUsers(response, getState()) : response);

      filteredRes
        .filter((d) => d.status !== "DONE")
        .forEach((d) => {
          if (d.project_id !== undefined && d.project_id !== null) {
            deployments[d.project_id] = d;
          } else {
            deployments[d.job_id + "_" + d.model_name] = d;
          }
        });

      deployments = transformDeployments(getState(), deployments);
      dispatch(updateDeployments(deployments));

      if (globalReducer.getIsTableauMode(getState())) {
        let jobs = [];
        let promises = [];

        response
          .filter((d) => d.status === "RUNNING")
          .forEach(
            (d) =>
              (runReducer.getJob(getState(), d.job_id) === undefined ||
                (Object.keys(runReducer.getJob(getState(), d.job_id)).length === 0 &&
                  d.job_id !== undefined &&
                  d.job_id !== null)) &&
              promises.push(ModelAPI.getRun(d.job_id).then((data) => jobs.push(data)))
          );

        Promise.all(promises).then(() => {
          if (jobs && jobs.length > 0) {
            return dispatch(runReducer.updateJobs(jobs));
          }
        });
      }

      return dispatch(globalReducer.updateIsDeploymentsLoading(false));
    });
  };
}

export function pollDeployments() {
  return (dispatch, getState) => {
    const deployments = globalReducer.getPendingDeployments(getState());

    const deploymentsArr = deployments
      ? Object.keys(deployments)
          .map((d) => ({ id: d, isPending: deployments[d] }))
          .filter((d) => d.isPending === true)
      : [];

    for (const deployment of deploymentsArr) {
      DeployAPI.getDeploy(deployment.id).then((response) => {
        response = transformDeployment(getState(), response);
        if (response.endpoint_url) {
          dispatch(globalReducer.removePendingDeployment(response.id));
        }
        return dispatch(updateDeployment(response));
      });
    }

    const groupDeployments = getGroupDeploymentsArr(getState());

    for (const deployment of groupDeployments) {
      if (deployment.status !== "ERROR" && deployment.status !== "RUNNING") {
        DeployAPI.getDeploy(deployment.id).then((response) => {
          return dispatch(updateGroupDeployment(response));
        });
      }
    }

    return Promise.resolve();
  };
}

export function stopDeploy(deployId, isGroup = false) {
  return (dispatch, getState) => {
    return DeployAPI.stopDeploy(deployId).then((response) => {
      if (response && response.job_id && response.model_name) {
        let groupCheck = false;
        if (isGroup) {
          const currGroup = getModelGroupDeployment(getState(), response.job_id, response.model_name);
          groupCheck = Boolean(currGroup);
        }
        return dispatch(deleteDeployment(response.job_id, response.model_name, groupCheck));
      }
      return response;
    });
  };
}

export function cancelDeploy(deployId) {
  return (dispatch, getState) =>
    DeployAPI.cancelDeploy(deployId).then((response) => response && dispatch(updateDeployment(response)));
}

export function openDeploy(deployment, viewMode) {
  return (dispatch, getState) => {
    if (deployment) {
      if (deployment.project_id) {
        dispatch(push("/project/" + deployment.project_id + (viewMode ? "/" + viewMode : "")));
      } else {
        dispatch(push("/run/" + deployment.job_id));

        dispatch(runReducer.updateShowDeploy(deployment.job_id, true));
        return dispatch(runReducer.updateFeatureSelection(deployment.job_id, deployment.model_name));
      }
    }
  };
}

export function handleRestfulProjectDeploy(projectId) {
  return (dispatch, getState) => {
    dispatch(updateDeployment({ project_id: projectId }));
    return ProjectAPI.deployProject(projectId).then((response) => {
      dispatch(globalReducer.addPendingDeployment(response.id));
      return dispatch(updateDeployment(response));
    });
  };
}

export function handleRestfulDeploy(jobId, modelName, endDate) {
  let options;
  // For deployment invocations (limited time for what-if)
  if (endDate) {
    if (endDate === true) {
      options = {
        end_date: moment().utc().add(30, "minutes").toISOString()
      };
    } else {
      options = { end_date: endDate };
    }
  }
  return (dispatch, getState) => {
    const model = runReducer.getModel(getState(), jobId, modelName);
    if (model && model.isAdvanced) {
      const i = modelName.lastIndexOf("-adv");
      modelName = modelName.substring(0, i);
    }
    // Currently disabled until backend deployment can handle the additional field
    // Advanced Model Deployment
    // DO NOT DELETE
    // if (model && model.adjustedThreshold !== undefined && model.adjustedThreshold !== null) {
    //   const adjustedThreshold = model.adjustedThreshold;
    //   options = {
    //     ...options,
    //     adjusted_threshold: adjustedThreshold
    //   }
    // }
    return DeployAPI.postDeploy(jobId, modelName, options).then((response) => {
      dispatch(globalReducer.addPendingDeployment(response.id));
      return dispatch(updateDeployment(response));
    });
  };
}

export function deployModelGroup(id) {
  return (dispatch, getState) => {
    dispatch(modelGroupReducer.updateIsLoading(id, true));

    const modelGroup = modelGroupReducer.getModelGroup(getState(), id);
    return ModelGroupAPI.deployModelGroup(modelGroup.id).then((response) => {
      return dispatch(updateGroupDeployment(response));
    });

    // const selModel = runReducer.getFeatureSelection(getState(), id);
    // const options = {
    //   head: {
    //     job_id: id,
    //     model_name: selModel || "aible-relative-impact"
    //   }
    // };
    // return ModelGroupAPI.patchModelGroup(modelGroup.id, options).then(response => {
    //   return ModelGroupAPI.deployModelGroup(modelGroup.id).then(response => {
    //     dispatch(modelGroupReducer.updateIsLoading(id, false));
    //     return dispatch(updateGroupDeployment(response));
    //   });
    // })
  };
}

export function handlePredictionResponse(response) {
  return (dispatch, getState) => {
    if (response && response.status && response.status === "INPROGRESS") {
      setTimeout(() => dispatch());
    }
  };
}

function getScoredFileName(file, name, isZip) {
  if (name) {
    return name;
  }
  let fileName = "ScoredFile.csv";
  if (file && file.name) {
    fileName = "scored_" + file.name;
  }
  return fileName;
}

export function createDatasetFromMonitor(projectId) {
  return (dispatch, getState) => {
    return ScoreAPI.createDatasetFromMonitor(projectId).then((response) => console.log(response));
  };
}

export function getPrediction(projectId, file, payload) {
  return async (dispatch, getState) => {
    const clientFactory = AibleClientFactory.getInstance();
    const project = projectReducer.getProject(getState(), projectId);

    return clientFactory
      .getAccountClient()
      .getCustomerClientFactory(project.account_id)
      .then((factory) => factory.getProjectClient().bulk_predict(projectId, file, payload))
      .catch((error) => {
        console.log(error);
        dispatch(projectReducer.updateIsScoringFile(projectId, false));
        throw error;
      });
  };
}

export function readCSV(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e && e.target && e.target.result;
      let originalCsvArray = [];
      if (text) {
        originalCsvArray = CSV.parse(text).filter((r) => Boolean(r) && Array.isArray(r) && r.some((e) => Boolean(e)));
      }
      return resolve(originalCsvArray);
    };
    reader.onerror = () => resolve([]);
    reader.readAsText(file);
  });
}

export function scoreFile(projectId, file, isMonitor = false) {
  return async (dispatch, getState) => {
    const isZip = file.type === "application/x-gzip" || String(file.name).slice(-2).toLowerCase() === "gz";

    dispatch(projectReducer.updateIsScoringFile(projectId, true));

    // const fileName = getScoredFileName(file, undefined, isZip);

    const readOptions = await getReadOptions(file, {
      compression: isZip ? "GZIP" : "NONE",
      encoding: undefined
    });

    try {
      const statusResponse = await dispatch(
        getPrediction(projectId, file, {
          options: {
            monitor: isMonitor,
            data_options: readOptions
          }
        })
      );

      dispatch(projectReducer.updateIsScoringFile(projectId, false));

      const state = getState();
      const account = accountsReducer.getSelectedAccount(state);
      const res = await AibleClientFactory.getInstance()
        .getAccountClient()
        .presignedGetRequest(account, statusResponse.prediction_url, "score_out.csv");

      await res.downloadFile();

      toast("Your file was scored and downloaded successfully.", {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });
    } catch (error) {
      console.log(error);

      let errorMessage;
      switch (error.status) {
        case 400:
          errorMessage = "There was an error reading your file. Please correct any errors and try again.";
          break;
        case 500:
        case 502:
          errorMessage = "There was an error processing your request.";
          break;
      }

      if (errorMessage !== undefined) {
        toast(errorMessage, {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });
      }

      dispatch(projectReducer.updateIsScoringFile(projectId, false));

      throw error;
    }
  };
}

export function getExecutorEndpoint(accountId) {
  return (dispatch, getState) => {
    const executorListener = (response) => {
      if (response && (response.status === "error" || response.endpoint_url)) {
        if (response.status === "error") {
          toast("Error in deployment executor!", {
            type: toast.TYPE.ERROR,
            position: toast.POSITION.TOP_CENTER,
            autoClose: 7500
          });
        }
        if (globalReducer.getIsCreatingExecutor(getState())) {
          dispatch(globalReducer.updateIsCreatingExecutor(false));
        }
        return Promise.resolve(response);
      } else {
        if (response && !globalReducer.getIsCreatingExecutor(getState())) {
          dispatch(globalReducer.updateIsCreatingExecutor(true));
        }
        return delay(3000)
          .then(() => ScoreAPI.postExecutor(accountId))
          .then((nResponse) => executorListener(nResponse));
      }
    };
    return ScoreAPI.getBulkScoreEndpoint(accountId).then((res) => {
      return executorListener(
        res && res.configuration && res.configuration.endpoint_url ? res.configuration : res.configuration.executor
      );
    });
  };
}
